import { computed, inject, Injectable } from '@angular/core';
import { injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { firstValueFrom } from 'rxjs';

import { Auth } from 'src/providers/auth/auth';

import { MonthlyFinanceApiService } from './monthly-finance-api.service';

import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';

export interface FinanceTypeOption {
  key: string;
  label: string;
  categoryKey: string;
  categoryLabel: string;
  subcategoryLabel: string;
}

export const financeTypesKeys = {
  all: ['finance-types'] as const,
};

/**
 * The backend has no finance-types endpoint; the Monthly P&L response already lists
 * every active type grouped by category, so the category pickers derive from it.
 * Cached for the session: the taxonomy does not change while the app is open.
 */
@Injectable({ providedIn: 'root' })
export class FinanceTypesService {
  private readonly monthlyFinanceApi = inject(MonthlyFinanceApiService);
  private readonly auth = inject(Auth);
  private readonly queryClient = injectQueryClient();

  readonly financeTypesQuery = injectQuery(() => ({
    queryKey: financeTypesKeys.all,
    queryFn: () => this.load(),
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
    enabled: this.auth.isAuthenticated(),
  }));

  readonly financeTypes = computed<FinanceTypeOption[]>(() => this.financeTypesQuery.data() ?? []);
  readonly selectItems = computed<SelectModel[]>(() => this.financeTypes().map(type => this.toSelectItem(type)));

  /** Resolves from the cache when present, otherwise fetches once. */
  getFinanceTypes(): Promise<FinanceTypeOption[]> {
    return this.queryClient.ensureQueryData({
      queryKey: financeTypesKeys.all,
      queryFn: () => this.load(),
      staleTime: Infinity,
    });
  }

  async getSelectItems(): Promise<SelectModel[]> {
    const types = await this.getFinanceTypes();
    return types.map(type => this.toSelectItem(type));
  }

  toSelectItem(type: FinanceTypeOption): SelectModel {
    return { value: type.key, viewValue: type.label, subtitle: type.subcategoryLabel };
  }

  private async load(): Promise<FinanceTypeOption[]> {
    const year = new Date().getFullYear();
    const pl = await firstValueFrom(this.monthlyFinanceApi.getProfitAndLoss({
      start_year: year,
      start_month: 1,
      end_year: year,
      end_month: 12,
    }));
    const types: FinanceTypeOption[] = [];
    for (const category of pl?.categories ?? []) {
      for (const subcategory of category.subcategories ?? []) {
        for (const item of subcategory.items ?? []) {
          types.push({
            key: item.key,
            label: item.label,
            categoryKey: category.key,
            categoryLabel: category.label,
            subcategoryLabel: subcategory.label,
          });
        }
      }
    }
    return types;
  }
}
